import React, { useEffect, useState } from "react";
import { useAsyncError, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Icon,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Skeleton,
  Avatar,
  Card,
  CardContent,
  CardActionArea
} from "@mui/material";
import {
  Event,
  Place,
  Person,
  PictureAsPdf,
  FactCheck,
  Pageview,
  Build,
  Flight,
  Groups,
  Article,
  SearchOff,
  DateRange
} from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import "dayjs/locale/id";
import axios from "axios";
import { API_URL, API_URL_PDF } from "../api";
import { CustomModal } from "../components/CustomModal";
import moment from "moment";
import "moment/locale/id";

export default function MenuRiwayatLaporan() {
  const navigate = useNavigate();
  // Filter Category State
  const [filterCategory, setFilterCategory] = useState('all');

  // Date Range State (using native Date for react-datepicker)
  const [dateRange, setDateRange] = useState([new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)]);
  const [startDate, endDate] = dateRange;

  // Temporary state for the picker (before clicking apply)
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [tempStartDate, tempEndDate] = tempDateRange;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  // Data State
  const [reports, setReports] = useState([]);
  const [p5mReports, setP5mReports] = useState([]);
  const [kerusakanReports, setKerusakanReports] = useState([]);
  const [checklistReports, setChecklistReports] = useState([]);
  const [inspectionReports, setInspectionReports] = useState([]);
  const [observationReports, setObservationReports] = useState([]);

  // Filtered State
  const [filteredReports, setFilteredReports] = useState({
    all: [],
    safety: [],
    harian: [],
    alat: []
  });

  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info",
  });

  moment.locale("id");

  const showModal = (title, message, type = "info") => {
    setModalConfig({ title, message, type });
    setModalVisible(true);
  };

  const handleFilterChange = (event, newCategory) => {
    if (newCategory !== null) {
      setFilterCategory(newCategory);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    filterReports();
  }, [dateRange, reports, p5mReports, kerusakanReports, checklistReports, inspectionReports, observationReports]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("userToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        axios.get(`${API_URL}/laporan-harian`, config),
        axios.get(`${API_URL}/p5m/reports`, config),
        axios.get(`${API_URL}/alat/reportskerusakan`, config),
        axios.get(`${API_URL}/drone-checklists`, config),
        axios.get(`${API_URL}/inspection-reports`, config),
        axios.get(`${API_URL}/observation-reports`, config)
      ]);

      setReports(r1.data || []);
      setP5mReports(r2.data?.data || []);
      setKerusakanReports(r3.data?.data || []);
      setChecklistReports(r4.data?.data || []);
      setInspectionReports(r5.data?.data || []);
      setObservationReports(r6.data?.data || []);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterReports = () => {
    const start = startDate ? moment(startDate) : moment().startOf('month');
    const end = endDate ? moment(endDate) : moment().endOf('month');

    const isInRange = (dateStr) => {
      if (!dateStr) return false;
      const d = moment(dateStr);
      return d.isSameOrAfter(start, 'day') && d.isSameOrBefore(end, 'day');
    };

    // Helper to add common props
    const mapReport = (r, type, title, color, icon) => ({
      ...r, type, title, color, icon, date: r.created_at || r.tanggal
    });

    // Safety Reports (Inspection & Observation)
    const safety = [
      ...inspectionReports.filter(r => isInRange(r.tanggal)).map(r => mapReport(r, 'inspection', 'Inspeksi', '#ff9800', <FactCheck />)),
      ...observationReports.filter(r => isInRange(r.tanggal)).map(r => mapReport(r, 'observation', 'Observasi', '#2196f3', <Pageview />))
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    // Harian Reports (P5M & Laporan Harian)
    const harian = [
      ...reports.filter(r => isInRange(r.created_at)).map(r => mapReport(r, 'harian', 'Harian', '#4aabff', <Article />)),
      ...p5mReports.filter(r => isInRange(r.tanggal)).map(r => mapReport(r, 'p5m', 'P5M', 'orange', <Groups />))
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    // Alat Reports (Kerusakan & Drone Checklist)
    const alat = [
      ...kerusakanReports.filter(r => isInRange(r.created_at)).map(r => mapReport(r, 'kerusakan', 'Kerusakan', 'red', <Build />)),
      ...checklistReports.filter(r => isInRange(r.created_at)).map(r => mapReport(r, 'checklist', 'Drone', 'green', <Flight />))
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    // All Reports (combined & sorted by time, no grouping)
    const all = [...safety, ...harian, ...alat].sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredReports({ all, safety, harian, alat });
  };

  const downloadPdf = async (e, item) => {
    e.stopPropagation();
    if (!item.file_path) {
      showModal("Info", "File PDF tidak tersedia untuk laporan ini", "warning");
      return;
    }

    // Force download using Blob
    try {
      const path = item.file_path.startsWith('/') ? item.file_path : `/${item.file_path}`;
      const url = `${API_URL_PDF}${path}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("userToken")}`
        }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = item.file_path.split('/').pop() || `laporan-${item.type}-${item.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      showModal("Error", "Gagal mengunduh file PDF", "error");
    }
  };

  // --- UI Components ---

  const ReportIcon = ({ type, icon, color }) => (
    <Avatar variant="rounded" sx={{ bgcolor: color, width: 48, height: 48, borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      {icon}
    </Avatar>
  );

  const ReportCard = ({ item }) => {
    const dateDisplay = moment(item.date).format("DD MMM YYYY");

    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: 3,
          border: '1px solid #f0f0f0',
          boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)"
          },
          overflow: 'visible'
        }}
        onClick={() => {
          if (item.type === 'inspection') navigate(`/detail-inspeksi/${item.id}`);
          else if (item.type === 'observation') navigate(`/detail-observasi/${item.id}`);
          else navigate(`/detail/${item.type}/${item.id}`);
        }}
      >
        <CardActionArea sx={{ p: 2, borderRadius: 3 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">

            {/* Icon Box */}
            <ReportIcon type={item.type} icon={item.icon} color={item.color} />

            {/* Content */}
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2 }}>
                  {item.title}
                </Typography>
                <Chip
                  label={item.type.toUpperCase()}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    bgcolor: `${item.color}15`,
                    color: item.color,
                    border: `1px solid ${item.color}30`
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Event sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight="500">
                  {dateDisplay}
                </Typography>


              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Place sx={{ fontSize: 14, color: item.lokasi ? 'text.secondary' : 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '180px' }}>
                  {item.lokasi || "Lokasi tidak tersedia"}
                </Typography>
              </Stack>

              {/* Specific: Pilot for Drone */}
              {item.type === 'checklist' && (
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                  <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {item.pilot}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          {/* Footer / Actions */}
          {item.file_path && (
            <Box onClick={(e) => downloadPdf(e, item)} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                icon={<PictureAsPdf sx={{ fontSize: '1.2rem !important' }} />}
                label="Unduh PDF"
                clickable
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: '8px',
                  borderColor: item.color,
                  color: item.color,
                  '& .MuiChip-icon': { color: item.color },
                  '&:hover': { bgcolor: `${item.color}10` }
                }}
              />
            </Box>
          )}

        </CardActionArea>
      </Card>
    );
  };

  const LoadingSkeleton = () => (
    <Stack spacing={2}>
      {[1, 2, 3].map(i => (
        <Paper key={i} sx={{ p: 2, borderRadius: 3, display: 'flex', gap: 2 }} elevation={0} variant="outlined">
          <Skeleton variant="rounded" width={48} height={48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
          </Box>
        </Paper>
      ))}
    </Stack>
  );

  const SectionHeader = ({ title, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
      <Box sx={{ width: 4, height: 24, bgcolor: color, mr: 1.5, borderRadius: 1 }} />
      <Typography variant="h6" fontWeight="800" sx={{ color: '#2c3e50', fontSize: '1.1rem' }}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <div className="form-container" style={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <header className="app-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate("/menu")}>
            <Icon>arrow_back</Icon>
          </button>
          <h1 className="header-title">Riwayat Laporan</h1>
          <div className="header-spacer"></div>
        </div>
      </header>

      <Box sx={{ width: "100%", mx: "auto", p: 2, pb: 12 }}>

        {/* Filter Controls */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, bgcolor: "white", borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>

          {/* Category Toggle */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: '#95a5a6', fontWeight: 'bold', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Kategori Laporan
            </Typography>
            <ToggleButtonGroup
              color="primary"
              value={filterCategory}
              exclusive
              onChange={handleFilterChange}
              fullWidth
              size="small"
              sx={{
                gap: 1,
                '& .MuiToggleButton-root': {
                  border: '1px solid #eef2f6',
                  borderRadius: '10px !important',
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#637381',
                  '&.Mui-selected': {
                    bgcolor: '#e3f2fd',
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': { bgcolor: '#bbdefb' }
                  }
                },
                '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                  borderLeft: '1px solid #eef2f6',
                  marginLeft: 0
                }
              }}
            >
              <ToggleButton value="all">Semua</ToggleButton>
              <ToggleButton value="safety">Safety</ToggleButton>
              <ToggleButton value="harian">Harian</ToggleButton>
              <ToggleButton value="alat">Alat</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Date Range - Travel App Style */}
          <Typography variant="caption" sx={{ color: '#95a5a6', fontWeight: 'bold', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Periode Filter
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 3,
            p: "12px 16px",
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#2196f3',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)'
            }
          }} onClick={() => {
            setTempDateRange(dateRange);
            setIsDatePickerOpen(true);
          }}>
            <DateRange sx={{ color: '#2196f3', mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: '#7f8c8d', display: 'block', lineHeight: 1 }}>
                Rentang Waktu
              </Typography>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#2c3e50', mt: 0.5 }}>
                {startDate && endDate
                  ? `${moment(startDate).format('DD MMM YYYY')} - ${moment(endDate).format('DD MMM YYYY')}`
                  : 'Pilih tanggal'}
              </Typography>
            </Box>
          </Box>

          {/* DatePicker Modal Overlay */}
          {isDatePickerOpen && (
            <Box sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }} onClick={() => setIsDatePickerOpen(false)}>
              <Box sx={{ bgcolor: 'white', borderRadius: 4, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                <DatePicker
                  selectsRange={true}
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  onChange={(update) => setTempDateRange(update)}
                  inline
                  monthsShown={1}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  yearDropdownItemNumber={5}
                  scrollableYearDropdown
                />
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #eee', bgcolor: 'white' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => setIsDatePickerOpen(false)}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Batal
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setDateRange(tempDateRange);
                      setIsDatePickerOpen(false);
                    }}
                    sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}
                  >
                    Terapkan
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          <style>{`
            .date-range-input {
              border: none;
              outline: none;
              font-size: 0.95rem;
              font-weight: 600;
              color: #2c3e50;
              width: 100%;
              background: transparent;
              cursor: pointer;
              margin-top: 2px;
            }
            .react-datepicker-wrapper {
              width: 100%;
            }
            .react-datepicker {
              font-family: 'Inter', 'Segoe UI', sans-serif;
              border: none;
              border-radius: 16px;
              box-shadow: 0 10px 40px rgba(0,0,0,0.12);
              overflow: hidden;
              background: white;
              min-width: 300px;
            }
            .react-datepicker__header {
              background: #1976d2;
              border-bottom: none;
              padding: 16px 12px 12px;
              border-radius: 0;
            }
            .react-datepicker__current-month {
              display: none;
            }
            
            /* Month & Year Dropdown Container */
            .react-datepicker__header__dropdown {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 8px;
              margin: 0;
              padding: 0 40px;
            }
            
            /* Dropdown Wrapper */
            .react-datepicker__month-dropdown-container,
            .react-datepicker__year-dropdown-container {
              position: relative;
            }
            
            /* Select Elements - Clean Simple Style */
            .react-datepicker__month-select,
            .react-datepicker__year-select {
              appearance: none;
              -webkit-appearance: none;
              -moz-appearance: none;
              background: transparent;
              border: none;
              outline: none;
              padding: 8px 24px 8px 8px;
              font-size: 1rem;
              font-weight: 600;
              color: white;
              cursor: pointer;
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
              background-repeat: no-repeat;
              background-position: right 4px center;
              background-size: 14px;
            }
            
            .react-datepicker__month-select:focus,
            .react-datepicker__year-select:focus {
              outline: none;
            }
            
            /* Option styling */
            .react-datepicker__month-select option,
            .react-datepicker__year-select option {
              background: white;
              color: #2c3e50;
              padding: 8px;
              font-weight: 500;
            }
            
            /* Hide day names row */
            .react-datepicker__day-names {
              display: none;
            }
            
            .react-datepicker__month {
              padding: 12px;
              margin: 0;
              background: white;
            }
            
            .react-datepicker__day {
              width: 38px;
              line-height: 38px;
              margin: 2px;
              font-weight: 500;
              color: #2c3e50;
              font-size: 0.9rem;
              border-radius: 8px;
              transition: all 0.15s ease;
            }
            
            .react-datepicker__day:hover {
              background: #e3f2fd;
              border-radius: 8px;
            }
            
            .react-datepicker__day--outside-month {
              color: #bdc3c7;
            }
            
            .react-datepicker__day--disabled {
              color: #e0e0e0 !important;
            }
            
            /* Selected & Range States */
            .react-datepicker__day--selected,
            .react-datepicker__day--range-end,
            .react-datepicker__day--range-start {
              background: #1976d2 !important;
              color: white !important;
              border-radius: 8px !important;
              font-weight: 600;
            }
            
            .react-datepicker__day--in-range {
              background: #e3f2fd !important;
              color: #1976d2 !important;
              border-radius: 0 !important;
            }
            
            .react-datepicker__day--in-selecting-range {
              background: #bbdefb !important;
              color: #1976d2 !important;
            }
            
            .react-datepicker__day--range-start {
              border-top-right-radius: 0 !important;
              border-bottom-right-radius: 0 !important;
            }
            
            .react-datepicker__day--range-end {
              border-top-left-radius: 0 !important;
              border-bottom-left-radius: 0 !important;
            }
            
            /* Navigation Arrows */
            .react-datepicker__navigation {
              top: 14px;
              width: 28px;
              height: 28px;
              background: transparent;
              border-radius: 6px;
              transition: all 0.15s ease;
            }
            
            .react-datepicker__navigation:hover {
              background: rgba(255,255,255,0.2);
            }
            
            .react-datepicker__navigation--previous {
              left: 8px;
            }
            
            .react-datepicker__navigation--next {
              right: 8px;
            }
            
            .react-datepicker__navigation-icon::before {
              border-color: white;
              border-width: 2px 2px 0 0;
              width: 8px;
              height: 8px;
            }
            
            .react-datepicker__triangle {
              display: none;
            }
            
            /* Today Highlight */
            .react-datepicker__day--today {
              background: #f5f5f5;
              font-weight: 700;
              color: #1976d2;
            }
            
            /* Keyboard Selected */
            .react-datepicker__day--keyboard-selected {
              background: #e3f2fd;
              color: #1976d2;
            }
            
            /* Close button */
            .react-datepicker__close-icon {
              display: none;
            }
            
            /* Animation */
            @keyframes fadeIn {
              from { opacity: 0; transform: scale(0.95); }
              to { opacity: 1; transform: scale(1); }
            }
            
            .react-datepicker {
              animation: fadeIn 0.15s ease-out;
            }
          `}</style>
        </Paper>

        {loading ? (
          <LoadingSkeleton />
        ) : (
          <Stack spacing={4}>

            {/* ALL Reports - No grouping, just sorted by time */}
            {filterCategory === 'all' && (
              <Box>
                {filteredReports.all.length > 0 ? (
                  filteredReports.all.map((item) => (
                    <ReportCard key={`${item.type}-${item.id}`} item={item} />
                  ))
                ) : (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'white', borderRadius: 3 }} elevation={0}>
                    <SearchOff sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Tidak ada laporan ditemukan</Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* Safety Reports */}
            {filterCategory === 'safety' && (
              <Box>
                <SectionHeader title="Laporan Safety" color="#2196f3" />

                {filteredReports.safety.length > 0 ? (
                  filteredReports.safety.map((item) => (
                    <ReportCard key={`${item.type}-${item.id}`} item={item} />
                  ))
                ) : (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'white', borderRadius: 3 }} elevation={0}>
                    <SearchOff sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Tidak ada laporan safety ditemukan</Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* Harian Reports (P5M & Laporan Harian) */}
            {filterCategory === 'harian' && (
              <Box>
                <SectionHeader title="Laporan Harian" color="#4aabff" />

                {filteredReports.harian.length > 0 ? (
                  filteredReports.harian.map((item) => (
                    <ReportCard key={`${item.type}-${item.id}`} item={item} />
                  ))
                ) : (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'white', borderRadius: 3 }} elevation={0}>
                    <SearchOff sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Tidak ada laporan harian ditemukan</Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* Alat Reports (Kerusakan & Drone) */}
            {filterCategory === 'alat' && (
              <Box>
                <SectionHeader title="Laporan Alat" color="#9e9e9e" />

                {filteredReports.alat.length > 0 ? (
                  filteredReports.alat.map((item) => (
                    <ReportCard key={`${item.type}-${item.id}`} item={item} />
                  ))
                ) : (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'white', borderRadius: 3 }} elevation={0}>
                    <SearchOff sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">Tidak ada laporan alat ditemukan</Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Stack>
        )}

        <CustomModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />
      </Box>
    </div >
  );
}
